import { HttpClient, HttpContext, HttpHandlerFn, HttpHeaders, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, Subject, switchMap, take, catchError, of } from 'rxjs';
import { jwtDecode } from "jwt-decode";
import { API_URL } from '@constants/api.constant';
import { environment } from '@environments/environment';
import { BYPASS_LOG } from '@constants/app.constant';
import { DecodedTokenModel, LoginResponseModel } from '@models/auth.models';
import { TimeUtils } from '@utils/time';
import { Router } from '@angular/router';
import { AppRoutes } from '@constants/app.route';


const context = new HttpContext().set(BYPASS_LOG, true);

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private refreshTokenInProgress = false;
    private refreshTokenSubject: Subject<boolean> = new BehaviorSubject<boolean>(false);



    // ========== AUTHENTICATION ==========
    public signIn(param: { grantType: string, authorization: string }) {
        return this.http.post<LoginResponseModel>(API_URL.getAuthToken, param, {
            //headers: this.getCommonHeaders(),
            context
        }).pipe(
            map((res: LoginResponseModel) => {
                this.setToken(res);
                return res;
            })
        );
    }

    // ========== REFRESH TOKEN ==========
    public refreshToken(request: HttpRequest<any>, next: HttpHandlerFn): Observable<any> {
        if (!this.isRefreshTokenValid()) {
            this.signOut();
            
        }
        if (!this.refreshTokenInProgress) {
            this.refreshTokenInProgress = true;
            this.refreshTokenSubject.next(true);
            let refreshToken = localStorage.getItem('@aion_refreshToken');
            return this.http.post<LoginResponseModel>(API_URL.getRefreshToken, JSON.stringify(refreshToken), {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                context
            }).pipe(
                switchMap((res: LoginResponseModel) => {
                    this.setToken(res);
                    this.refreshTokenInProgress = false;
                    this.refreshTokenSubject.next(false);
                    return next(this.addAuthToken(request));
                })
            );
        } else {
            return this.refreshTokenSubject.pipe(
                filter(result => !result),
                take(1),
                switchMap(() => next(this.addAuthToken(request)))
            );
        }
    }

    public cleanRefreshToken() {
        let refreshToken = localStorage.getItem('@aion_refreshToken');
        return this.http.post<LoginResponseModel>(API_URL.getRefreshToken, JSON.stringify(refreshToken), {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            context
        })
    }

    // ========== TOKEN UTILS ==========
    // private getCommonHeaders(): HttpHeaders {
    //     return new HttpHeaders({ 'Content-Type': 'application/json' });
    // }

    private getFullAuthHeaders(): HttpHeaders {
        const accessToken = localStorage.getItem('@aion_accessToken') ?? '';
        const userDetail = localStorage.getItem('@aion_userDetails');
        const subscriptionKey = userDetail ? JSON.parse(userDetail).subscriptionKey : '';

        return new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
            'ocp-apim-subscription-key': subscriptionKey
        });
    }

    addAuthToken(request: HttpRequest<any>) {
        const headersObj = this.getFullAuthHeaders();
        const headersPlain: { [key: string]: string } = {};

        headersObj.keys().forEach(key => {
            const val = headersObj.get(key);
            if (val !== null) {
                headersPlain[key] = val;
            }
        });

        return request.clone({
            setHeaders: headersPlain
        });
    }

    public setToken(res: LoginResponseModel) {
        const decodedJwt = jwtDecode<DecodedTokenModel>(res.accessToken);
        localStorage.setItem('@aion_accessToken', res.accessToken);
        if(res.refreshToken) localStorage.setItem('@aion_refreshToken', res.refreshToken);
        localStorage.setItem('@aion_expiresAt', decodedJwt.exp.toString());
        localStorage.setItem('@aion_userDetails', JSON.stringify(decodedJwt));
    }

    public deleteToken() {
        localStorage.removeItem('@aion_accessToken');
        localStorage.removeItem('@aion_refreshToken');
        localStorage.removeItem('@aion_expiresAt');
        localStorage.removeItem('@aion_userDetails');
    }


    public signOut(){
        this.deleteToken();
        this.router.navigate([AppRoutes.AUTH]);
    }

    // ========== SESSION & AUTH INFO ==========
    public isRefreshTokenValid(): boolean {
        return this.getCurrTime() < this.getRefreshTokenTime();
    }

    public isLoggedIn(): boolean {
        return this.getCurrTime() < this.getTokenTime();
    }

    private getCurrTime() {
        // Aggiunge 3 secondi per evitare chiamate su token in scadenza
        return TimeUtils.convertToTimestamp(TimeUtils.dateAdd(new Date(), 0, 0, 0, 0, 0, 3).toISOString());
    }

    public getRefreshTokenTime(): number {
        try {
            const rft = localStorage.getItem('@aion_refreshToken');
            if (!rft) return 0;
            const decoded = jwtDecode<DecodedTokenModel>(rft);
            return decoded?.exp ? decoded.exp * 1000 : 0;
        } catch {
            return 0;
        }
    }


    public getTokenTime() {
        const exp = localStorage.getItem('@aion_expiresAt');
        return exp ? +exp * 1000 : 0;
    }

    public getToken() {
        return localStorage.getItem('@aion_accessToken') ?? null;
    }

    public getTokenUserInformations() {
        const userInfo = localStorage.getItem('@aion_userDetails');
        return userInfo ? JSON.parse(userInfo) : null;
    }

    // ========== IMPERSONATION ==========
    // public impersonateUser(mail: string) {
    //     return this.http.post<LoginResponseModel>(`${API_URL.impersonateUser}${mail}`, {}, {
    //         responseType: 'json'
    //     });
    // }

    // public stopImpersonateUser() {
    //     return this.http.post<any>(API_URL.impersonateUserLogout, {}, {
    //         responseType: 'json'
    //     });
    // }

    // // ========== ACCOUNT ==========
    // public deleteUser(obj: { reason: string | null }) {
    //     return this.http.post<string>(API_URL.deleteUser, obj, {
    //         headers: this.getCommonHeaders(),
    //         observe: 'body'
    //     });
    // }


    public waitForValidToken(): Observable<void> {
        if (this.isLoggedIn()) {
            // Token già valido
            return new Observable(observer => {
                observer.next();
                observer.complete();
            });
        }

        // Attendi che il refresh sia terminato e che il token diventi valido
        return this.refreshTokenSubject.pipe(
            filter((inProgress) => !inProgress),
            filter(() => this.isLoggedIn()), // appena torna valido
            take(1),
            map(() => void 0)
        );
    }
}
